import React, { useState } from 'react';
import { Product, Ingredient, SavedLabel } from './types';
import { ProductList } from './components/ProductList';
import { IngredientList } from './components/IngredientList';
import { useLocalStorage } from './hooks/useLocalStorage';
import { createSampleData } from './data/sampleData';
import { ShoppingBag, Package, Sparkles } from 'lucide-react';
import { Canvas, Rect, Text, Textbox, Line } from 'fabric';
import { translateLabel, translateAllergen, isRTL } from './utils/translations';
import { translateIngredient, translateProduct } from './utils/ingredientTranslations';
import { ImpressumModal } from './components/ImpressumModal';
import { DatenschutzModal } from './components/DatenschutzModal';
export function App() {
  const [products, setProducts] = useLocalStorage<Product[]>('products', []);
  const [ingredients, setIngredients] = useLocalStorage<Ingredient[]>('ingredients', []);
  const [activeTab, setActiveTab] = useState<'products' | 'ingredients'>('products');
  const [showImpressum, setShowImpressum] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);
  // Product handlers
  const handleAddProduct = (productData: Omit<Product, 'id' | 'createdAt'>) => {
    const newProduct: Product = {
      ...productData,
      id: crypto.randomUUID(),
      labels: [],
      createdAt: new Date().toISOString()
    };
    setProducts([...products, newProduct]);
  };
  const handleEditProduct = (id: string, productData: Omit<Product, 'id' | 'createdAt'>) => {
    setProducts(products.map(p => p.id === id ? {
      ...p,
      ...productData
    } : p));
  };
  const handleDeleteProduct = (id: string) => {
    setProducts(products.filter(p => p.id !== id));
  };
  const handleSaveLabel = (productId: string, labelData: Omit<SavedLabel, 'id' | 'createdAt' | 'updatedAt'>) => {
    setProducts(products.map(p => {
      if (p.id !== productId) return p;
      const currentLabels = p.labels || [];
      const existingLabelIndex = currentLabels.findIndex(l => l.language === labelData.language);
      const now = new Date().toISOString();
      if (existingLabelIndex >= 0) {
        const updatedLabels = [...currentLabels];
        updatedLabels[existingLabelIndex] = {
          ...labelData,
          id: updatedLabels[existingLabelIndex].id,
          createdAt: updatedLabels[existingLabelIndex].createdAt,
          updatedAt: now
        };
        return {
          ...p,
          labels: updatedLabels
        };
      } else {
        const newLabel: SavedLabel = {
          ...labelData,
          id: crypto.randomUUID(),
          createdAt: now,
          updatedAt: now
        };
        return {
          ...p,
          labels: [...currentLabels, newLabel]
        };
      }
    }));
  };
  const handleRefreshAllLabels = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (!product || !product.labels || product.labels.length === 0) return;
    // Regenerate all labels with current product data
    const updatedLabels = product.labels.map(label => {
      const canvas = new Canvas(null as any, {
        width: 400,
        height: 600
      });
      // Recreate label with current data
      createLabelOnCanvas(canvas, product, ingredients, label.language);
      const canvasJSON = canvas.toJSON(['selectable', 'evented']);
      const canvasData = JSON.stringify(canvasJSON);
      const thumbnail = canvas.toDataURL({
        format: 'png',
        quality: 0.8,
        multiplier: 0.5
      });
      canvas.dispose();
      return {
        ...label,
        canvasData,
        thumbnail,
        updatedAt: new Date().toISOString()
      };
    });
    setProducts(products.map(p => p.id === productId ? {
      ...p,
      labels: updatedLabels
    } : p));
  };
  // Helper function to create label on canvas
  const createLabelOnCanvas = (canvas: Canvas, product: Product, allIngredients: Ingredient[], language: string) => {
    const rtl = isRTL(language as any);
    const productIngredients = product.ingredientIds.map(id => allIngredients.find(i => i.id === id)).filter(Boolean) as Ingredient[];
    const allergenIds = new Set<string>();
    productIngredients.forEach(ing => {
      ing.allergens.forEach(a => allergenIds.add(a));
    });
    const border = new Rect({
      left: 10,
      top: 10,
      width: 380,
      height: 580,
      fill: 'transparent',
      stroke: '#000000',
      strokeWidth: 2,
      rx: 5,
      ry: 5,
      selectable: false
    });
    const translatedProductName = translateProduct(product.name, language as any);
    const productName = new Textbox(translatedProductName.toUpperCase(), {
      left: 200,
      top: 40,
      width: 340,
      fontSize: 32,
      fill: '#000000',
      fontFamily: 'Arial',
      fontWeight: 'bold',
      textAlign: 'center',
      originX: 'center'
    });
    const ingredientsLabelText = translateLabel('ingredients', language as any) + ':';
    const ingredientsLabel = new Text(ingredientsLabelText, {
      left: rtl ? 360 : 40,
      top: 120,
      fontSize: 14,
      fill: '#000000',
      fontFamily: 'Arial',
      fontWeight: 'bold',
      originX: rtl ? 'right' : 'left'
    });
    let ingredientsText = '';
    const boldRanges: {
      start: number;
      end: number;
    }[] = [];
    productIngredients.forEach((ing, index) => {
      const startPos = ingredientsText.length;
      if (index > 0) {
        ingredientsText += ', ';
      }
      const translatedName = translateIngredient(ing.name, language as any);
      ingredientsText += translatedName;
      if (ing.allergens.length > 0) {
        const ingredientStart = startPos + (index > 0 ? 2 : 0);
        const ingredientEnd = ingredientsText.length;
        boldRanges.push({
          start: ingredientStart,
          end: ingredientEnd
        });
      }
    });
    const ingredientsTextbox = new Textbox(ingredientsText, {
      left: rtl ? 360 : 40,
      top: 145,
      width: 320,
      fontSize: 13,
      fill: '#000000',
      fontFamily: 'Arial',
      textAlign: rtl ? 'right' : 'left',
      originX: rtl ? 'right' : 'left'
    });
    boldRanges.forEach(range => {
      for (let i = range.start; i < range.end; i++) {
        ingredientsTextbox.setSelectionStyles({
          fontWeight: 'bold'
        }, i, i + 1);
      }
    });
    let currentY = 240;
    if (allergenIds.size > 0) {
      const allergensList = Array.from(allergenIds).map(id => translateAllergen(id, language as any)).join(', ');
      const allergensWarning = new Textbox(translateLabel('contains', language as any) + ': ' + allergensList, {
        left: rtl ? 360 : 40,
        top: currentY,
        width: 320,
        fontSize: 11,
        fill: '#000000',
        fontFamily: 'Arial',
        fontWeight: 'bold',
        textAlign: rtl ? 'right' : 'left',
        originX: rtl ? 'right' : 'left'
      });
      canvas.add(allergensWarning);
      currentY += 60;
    }
    const bestBeforeLabel = new Text(language === 'de' ? 'Mindestens haltbar bis:' : language === 'en' ? 'Best before:' : language === 'fr' ? 'À consommer avant le:' : language === 'es' ? 'Consumir preferentemente antes del:' : language === 'it' ? 'Da consumarsi preferibilmente entro:' : language === 'ar' ? 'يفضل استهلاكه قبل:' : 'Best before:', {
      left: 200,
      top: currentY + 20,
      fontSize: 12,
      fill: '#000000',
      fontFamily: 'Arial',
      textAlign: 'center',
      originX: 'center'
    });
    const bestBeforeDate = new Text('31.12.2025', {
      left: 200,
      top: currentY + 45,
      fontSize: 18,
      fill: '#000000',
      fontFamily: 'Arial',
      fontWeight: 'bold',
      textAlign: 'center',
      originX: 'center'
    });
    const barcodeY = currentY + 90;
    const barcodeLines: Line[] = [];
    const barcodeWidth = 200;
    const barcodeHeight = 60;
    const barcodeX = 100;
    const lineCount = 40;
    for (let i = 0; i < lineCount; i++) {
      const lineWidth = Math.random() > 0.5 ? 2 : 1;
      const line = new Line([barcodeX + i * (barcodeWidth / lineCount), barcodeY, barcodeX + i * (barcodeWidth / lineCount), barcodeY + barcodeHeight], {
        stroke: '#000000',
        strokeWidth: lineWidth,
        selectable: false
      });
      barcodeLines.push(line);
    }
    const barcodeNumber = new Text('4123456789018', {
      left: 200,
      top: barcodeY + barcodeHeight + 5,
      fontSize: 12,
      fill: '#000000',
      fontFamily: 'Courier New',
      textAlign: 'center',
      originX: 'center',
      selectable: false
    });
    const companyName = new Text('Ihre Firma GmbH', {
      left: 200,
      top: 520,
      fontSize: 10,
      fill: '#000000',
      fontFamily: 'Arial',
      textAlign: 'center',
      originX: 'center'
    });
    const companyAddress = new Text('Musterstraße 123', {
      left: 200,
      top: 540,
      fontSize: 9,
      fill: '#000000',
      fontFamily: 'Arial',
      textAlign: 'center',
      originX: 'center'
    });
    const companyCity = new Text('D-12345 Musterstadt', {
      left: 200,
      top: 555,
      fontSize: 9,
      fill: '#000000',
      fontFamily: 'Arial',
      textAlign: 'center',
      originX: 'center'
    });
    canvas.add(border);
    canvas.add(productName);
    canvas.add(ingredientsLabel);
    canvas.add(ingredientsTextbox);
    canvas.add(bestBeforeLabel);
    canvas.add(bestBeforeDate);
    barcodeLines.forEach(line => canvas.add(line));
    canvas.add(barcodeNumber);
    canvas.add(companyName);
    canvas.add(companyAddress);
    canvas.add(companyCity);
  };
  // Ingredient handlers
  const handleAddIngredient = (ingredientData: Omit<Ingredient, 'id' | 'createdAt'>) => {
    const newIngredient: Ingredient = {
      ...ingredientData,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString()
    };
    setIngredients([...ingredients, newIngredient]);
  };
  const handleEditIngredient = (id: string, ingredientData: Omit<Ingredient, 'id' | 'createdAt'>) => {
    setIngredients(ingredients.map(i => i.id === id ? {
      ...i,
      ...ingredientData
    } : i));
  };
  const handleDeleteIngredient = (id: string) => {
    setProducts(products.map(p => ({
      ...p,
      ingredientIds: p.ingredientIds.filter(iId => iId !== id)
    })));
    setIngredients(ingredients.filter(i => i.id !== id));
  };
  const handleLoadSampleData = () => {
    const {
      ingredients: sampleIngredients,
      product: sampleProduct
    } = createSampleData();
    setIngredients([...ingredients, ...sampleIngredients]);
    setProducts([...products, sampleProduct]);
    setActiveTab('products');
  };
  const hasSampleData = products.some(p => p.name === 'Gouda Käse');
  return <div className="min-h-screen bg-gray-50" data-id="element-0">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40" data-id="element-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2">
          <div className="flex items-center justify-between h-16" data-id="element-3">
            <div className="flex items-center gap-3" data-id="element-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center" data-id="element-5">
                <ShoppingBag className="w-6 h-6 text-white" data-id="element-6" />
              </div>
              <div data-id="element-7">
                <h1 className="text-xl font-bold text-gray-900" data-id="element-8">
                  Produkt Manager
                </h1>
                <p className="text-xs text-gray-500" data-id="element-9">
                  Zutaten & Allergenverwaltung
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-10">
              {!hasSampleData && (products.length === 0 || ingredients.length === 0) && <button onClick={handleLoadSampleData} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium" data-id="element-11">
                    <Sparkles className="w-4 h-4" data-id="element-12" />
                    Beispiel-Produkt laden
                  </button>}
              <button onClick={() => setShowDatenschutz(true)} className="text-sm text-gray-500 hover:text-gray-700 transition-colors" data-id="element-13">
                Datenschutz
              </button>
              <button onClick={() => setShowImpressum(true)} className="text-sm text-gray-500 hover:text-gray-700 transition-colors" data-id="element-14">
                Impressum
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-15">
        <div className="mb-6" data-id="element-16">
          <div className="border-b border-gray-200" data-id="element-17">
            <nav className="-mb-px flex gap-8" data-id="element-18">
              <button onClick={() => setActiveTab('products')} className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'products' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-19">
                <ShoppingBag className="w-5 h-5" data-id="element-20" />
                Produkte
                <span className="ml-2 py-0.5 px-2 rounded-full bg-gray-100 text-gray-600 text-xs font-medium" data-id="element-21">
                  {products.length}
                </span>
              </button>
              <button onClick={() => setActiveTab('ingredients')} className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'ingredients' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-22">
                <Package className="w-5 h-5" data-id="element-23" />
                Zutaten
                <span className="ml-2 py-0.5 px-2 rounded-full bg-gray-100 text-gray-600 text-xs font-medium" data-id="element-24">
                  {ingredients.length}
                </span>
              </button>
            </nav>
          </div>
        </div>

        {activeTab === 'products' ? <ProductList products={products} ingredients={ingredients} onAdd={handleAddProduct} onEdit={handleEditProduct} onDelete={handleDeleteProduct} onSaveLabel={handleSaveLabel} onRefreshAllLabels={handleRefreshAllLabels} data-id="element-25" /> : <IngredientList ingredients={ingredients} onAdd={handleAddIngredient} onEdit={handleEditIngredient} onDelete={handleDeleteIngredient} data-id="element-26" />}
      </div>
      {showImpressum && <ImpressumModal onClose={() => setShowImpressum(false)} data-id="element-27" />}
      {showDatenschutz && <DatenschutzModal onClose={() => setShowDatenschutz(false)} data-id="element-28" />}
    </div>;
}